/** Loads LiveChat once at document level, outside the application's content shell. */

type LiveChatConfig = {
  license: number;
  group?: number;
  chat_between_groups?: boolean;
  integration_name?: string;
  product_name?: string;
};

type LiveChatApi = {
  _q: unknown[][];
  _h: ((method: string, args: unknown[]) => unknown) | null;
  _v: string;
  on: (...args: unknown[]) => void;
  once: (...args: unknown[]) => void;
  off: (...args: unknown[]) => void;
  get: (...args: unknown[]) => unknown;
  call: (...args: unknown[]) => void;
  init: () => void;
};

declare global {
  interface Window {
    __lc?: LiveChatConfig;
    LiveChatWidget?: LiveChatApi;
  }
}

export const LIVECHAT_SCRIPT_SRC = 'https://cdn.livechatinc.com/tracking.js';
const LIVECHAT_SCRIPT_ID = 'lyx-livechat-widget';

function numericId(value: string): number | null {
  if (!/^\d+$/.test(value.trim())) return null;
  const parsed = Number(value);
  return Number.isSafeInteger(parsed) && parsed >= 0 ? parsed : null;
}

function createQueuedApi(): LiveChatApi {
  const api: LiveChatApi = {
    _q: [],
    _h: null,
    _v: '2.0',
    on: (...args) => enqueue('on', args),
    once: (...args) => enqueue('once', args),
    off: (...args) => enqueue('off', args),
    get: (...args) => {
      if (!api._h) throw new Error("[LiveChatWidget] You can't use getters before load.");
      return enqueue('get', args);
    },
    call: (...args) => enqueue('call', args),
    init: () => {
      if (document.getElementById(LIVECHAT_SCRIPT_ID)) return;
      const script = document.createElement('script');
      script.id = LIVECHAT_SCRIPT_ID;
      script.async = true;
      script.type = 'text/javascript';
      script.src = LIVECHAT_SCRIPT_SRC;
      document.head.appendChild(script);
    },
  };

  function enqueue(method: string, args: unknown[]): undefined {
    if (api._h) {
      api._h(method, args);
      return undefined;
    }
    api._q.push([method, args]);
    return undefined;
  }

  return api;
}

export function ensureLiveChatWidget(licenseId: string, groupId = ''): boolean {
  const license = numericId(licenseId);
  if (license === null || license === 0) return false;

  const group = numericId(groupId);
  window.__lc = {
    ...window.__lc,
    license,
    integration_name: 'manual_channels',
    product_name: 'livechat',
    ...(group !== null ? { group, chat_between_groups: false } : null),
  };
  window.LiveChatWidget ??= createQueuedApi();
  window.LiveChatWidget.init();
  document.documentElement.classList.add('lyx-livechat-enabled');
  return true;
}
